import React, { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useParams } from 'react-router-dom';
import RequireAuth from './auth/RequireAuth.jsx';
import { useAuth } from './auth/AuthProvider.jsx';
import { Login, Verify } from './auth/screens.jsx';
import CarrierSetup from './carrier/Setup.jsx';
import CarrierDispatch from './carrier/Dispatch.jsx';
import BrokerOnboarding from './broker/Onboarding.jsx';
import BrokerBoard from './broker/Board.jsx';
import Apply from './apply/Apply.jsx';
import RequireStaff from './staff/RequireStaff.jsx';
import StaffCarriers from './staff/Carriers.jsx';
import StaffCarrierSetup from './staff/CarrierSetup.jsx';
import StaffSetupEmail from './staff/SetupEmail.jsx';
import CarrierApp from './carrier/pages/CarrierApp.jsx';
import OptIn from './carrier/pages/OptIn.jsx';
import { attachSaver, hydrate, getSaveError, subscribe } from './shared/brokerConfig.js';
import { freight } from './lib/freightApi.js';

// ALFRED FREIGHT — the vertical app, split out of the operating dashboard.
//
// Two verticals, ONE app, deliberately:
//   carrier — a company running its own trucks (drivers, loads, check calls)
//   broker  — a brokerage covering loads with other people's trucks
// They share most of the domain (loads, stops, check calls, detention, documents,
// escalation ladders). Two codebases would mean building that twice and watching
// them drift, which is this repo's demonstrated failure mode — a hand-copied
// vendor list in a second file is exactly what produced the Cartesia bug.
//
// THE FRONT DOOR IS ONE QUESTION. Brian, 2026-08-12: "we should have a selection
// that asks them to click a carrier button or a broker button, and then it will take
// them to the appropriate area." Nobody arriving here is both. Showing all four
// screens at once made a reader work out which half applied to them before they
// could start; asking which business they are first is one decision instead of four.
//
// AUTH IS THIS APP'S OWN, AND THAT IS THE POINT OF THE SPLIT.
//
// This used to sign in against the voice platform's core, which meant core kept a
// central enum of every app that might ask and a URL for each. When the client forgot
// to name itself — which it did — core mailed people a link to a different product,
// while this screen cheerfully said "check your email". It stayed broken for weeks
// because a login that always claims success cannot tell you it failed.
//
// Now: one backend, one frontend, one link target. Adding a teammate is a Railway
// variable edit (ALLOWED_EMAILS), and nothing here can reach — or be broken by — the
// live calling platform.
//
// ⛔ THE TWO SIDES ARE NO LONGER IN THE SAME STATE, SO THE CHROME MUST NOT CLAIM THEY ARE.
//
// The CARRIER screens are wired: setup writes to this backend, the dispatch board reads
// real drivers, loads and call history, and pressing Call places an actual phone call. The
// BROKERAGE board is PART wired as of 2026-10-08 — the carrier file reads the brokerage's
// real book and approve/block writes to it — while its loads, customers, handoff queue,
// risk signals and call log are still arrays declared inside Board.jsx. A single
// "prototype" badge across both was honest when both were fake; now it reads as a lie on
// one side and cover on the other, so the badge belongs to the vertical.
//
// ⚠️ Removing the badge from a screen is a claim about that screen. It comes off when the
// screen is wired, not when it looks finished.

const sans = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif';
const mono = 'ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace';
const T = { ink: '#000', ink2: '#5A5A5A', ink3: '#8C8C8C', ink4: '#BFBFBF', hair: '#DCDCDC', hairSoft: '#EDEDED', wash: '#F5F5F5' };

const VERTICALS = {
  carrier: {
    label: 'Carrier',
    tagline: 'I run my own trucks.',
    // Wired to this backend end to end, which is why these screens carry no prototype badge.
    live: true,
    blurb: 'Drivers, loads, check calls, and the handoff queue when Alfred needs a person.',
    // No index of screens: /carrier is the carrier's own Home now (carrier/pages), and the setup
    // and the old board are linked from the front door for our own accounts.
    screens: []
  },
  broker: {
    label: 'Brokerage',
    tagline: 'I cover loads with other people’s trucks.',
    // ⛔ PART WIRED, SO THE BADGE STAYS. The Carriers tab reads and writes the real book;
    // Loads, Customers, Overview and History still render seed arrays. The badge is a claim
    // about the whole vertical, and most of it is still sample data — it comes off when the
    // loads and customers are wired too, not when the first tab is.
    live: false,
    blurb: 'Carriers, vetting, customers, and the handoff queue when Alfred needs a person.',
    screens: [
      { to: '/broker/setup', label: 'Brokerage setup', note: 'TMS connection, scope, team, carrier-vetting rules.' },
      { to: '/broker/board', label: 'Brokerage board', note: 'Loads, carriers, customers, the handoff queue.' }
    ]
  }
};

function Shell({ children, prototype = true }) {
  return (
    <div style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
      <header style={{ borderBottom: `1px solid ${T.hair}`, background: '#fff' }}>
        <div style={{ maxWidth: 880, margin: '0 auto', padding: '0 22px', height: 54, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/" style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4, color: '#000', textDecoration: 'none' }}>Alfred</Link>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>Freight</span>
          {prototype && <span style={{
            marginLeft: 'auto', fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: 'uppercase',
            border: '1px solid #000', borderRadius: 3, padding: '1px 5px'
          }}>prototype</span>}
        </div>
      </header>
      <main style={{ maxWidth: 880, margin: '0 auto', padding: '32px 22px 70px' }}>{children}</main>
    </div>
  );
}

/**
 * Who is signed in, from the server: { email, staff, via }. A carrier's invited person ('member')
 * belongs to one carrier, so the app takes them straight to it instead of asking which side they
 * are on, and our staff get the way into the staff area.
 */
function useMe() {
  const { isAuthenticated } = useAuth();
  const [me, setMe] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) { setMe(null); return undefined; }
    let alive = true;
    freight.me().then((m) => alive && setMe(m)).catch(() => {});
    return () => { alive = false; };
  }, [isAuthenticated]);
  return me;
}

/**
 * A carrier's own person never sees the front door, the brokerage half, the self-serve setup or the
 * old board: their pages are under /carrier.
 */
function NotForMembers({ children }) {
  const me = useMe();
  if (me?.via === 'member') return <Navigate to="/carrier" replace />;
  return children;
}

/** The whole front door: which of the two are you? */
function Chooser() {
  const me = useMe();
  return (
    // No badge in the header here: this page is about BOTH sides, and a badge spanning the
    // header would say the carrier screens are a prototype. The card for the side that is
    // one carries the chip instead, which is the specific claim.
    <Shell prototype={false}>
      <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.7 }}>Which one are you?</div>
      <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 7, lineHeight: 1.55, maxWidth: 520 }}>
        Pick a side and we’ll take you to its screens. The carrier screens are connected and
        place real calls. On the brokerage side the carrier file, the customers and the loads
        are live — the handoff queue, risk signals, call log and overview figures are still
        sample data, and nothing there places a call yet, so it keeps the prototype badge.
      </div>

      {me?.staff && (
        <Link to="/staff" className="alf-choice" style={{ display: 'block', marginTop: 18, padding: '12px 16px', border: `1px solid ${T.hair}`,
          borderRadius: 10, background: '#fff', color: '#000', textDecoration: 'none', fontSize: 13.5 }}>
          <span style={{ fontWeight: 600 }}>Our admin</span>
          <span style={{ color: T.ink2 }}> · set up carriers and send the setup email ›</span>
        </Link>
      )}

      <div style={{ display: 'flex', gap: 14, marginTop: 26, flexWrap: 'wrap' }}>
        {Object.entries(VERTICALS).map(([key, v]) => (
          <Link
            key={key}
            to={`/${key === 'broker' ? 'broker' : 'carrier'}`}
            className="alf-choice"
            style={{
              flex: '1 1 300px', minWidth: 260, textDecoration: 'none', color: '#000',
              background: '#fff', border: `1px solid ${T.hair}`, borderRadius: 12,
              padding: '22px 22px 20px', display: 'block'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 19, fontWeight: 600, letterSpacing: -0.45 }}>{v.label}</span>
              {/* Which side is real, visible before you pick one. */}
              {!v.live && <span style={{
                fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: 'uppercase',
                border: `1px solid ${T.ink3}`, color: T.ink2, borderRadius: 3, padding: '1px 5px'
              }}>prototype</span>}
            </div>
            <div style={{ fontSize: 14, color: T.ink, marginTop: 6 }}>{v.tagline}</div>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 8, lineHeight: 1.5 }}>{v.blurb}</div>
            <div style={{ fontSize: 12.5, marginTop: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ borderBottom: '1px solid #000', paddingBottom: 1 }}>Open</span>
              <span style={{ color: T.ink4 }}>›</span>
            </div>
          </Link>
        ))}
      </div>
      {/* Ours only (members never see this page): the carrier screens from before its own pages. */}
      <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 16, lineHeight: 1.6 }}>
        Also for a carrier: <Link to="/carrier/board" style={{ color: T.ink2 }}>the full board</Link> (the one before its own pages)
        {' · '}<Link to="/carrier/setup" style={{ color: T.ink2 }}>self-serve setup</Link>
      </div>
    </Shell>
  );
}

/** One vertical's screens — two rows, nothing from the other side. */
function VerticalIndex() {
  const { vertical } = useParams();
  const v = VERTICALS[vertical];
  // An unknown segment just shows the question again. Rendering the chooser beats
  // <Navigate> here: no redirect round-trip, and it still works where effects don't run.
  if (!v) return <Chooser />;

  return (
    <Shell prototype={!v.live}>
      <Link to="/" style={{ fontSize: 12.5, color: T.ink3 }}>← Not a {v.label.toLowerCase()}?</Link>
      <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.7, marginTop: 12 }}>{v.label}</div>
      <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 6, lineHeight: 1.55, maxWidth: 520 }}>{v.blurb}</div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: 'hidden', marginTop: 20, background: '#fff' }}>
        {v.screens.map((s, i) => (
          <Link
            key={s.to}
            to={s.to}
            className="alf-choice"
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px',
              borderTop: i === 0 ? 'none' : `1px solid ${T.hairSoft}`, textDecoration: 'none', color: '#000'
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 15, fontWeight: 500, letterSpacing: -0.25 }}>{s.label}</span>
              <span style={{ display: 'block', fontSize: 12.5, color: T.ink3, marginTop: 2 }}>{s.note}</span>
            </span>
            <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
          </Link>
        ))}
      </div>
    </Shell>
  );
}

/**
 * ⛔ THE BROKERAGE CONFIG IS ADOPTED BEFORE THE BOARD RENDERS ITS OWN DEFAULTS.
 *
 * `brokerConfig.js` kept the result of onboarding in a module variable, so a brokerage that
 * finished setup was un-set-up again after a refresh — the team, the branches, the scope, the
 * escalation ladder and every vetting rule, gone. It is a row now, and this is where the row
 * is fetched and where the store is given a way to write it back.
 *
 * The fetch is injected rather than done inside the store because that module is imported by
 * the render tests and by module-level board code; reaching for the network at import time
 * would make both depend on a server.
 */
function useBrokerageConfig() {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState({ loading: !!isAuthenticated, error: null });
  useEffect(() => {
    // ⛔ NOT BEFORE THERE IS A SESSION. The magic link opens /verify as a NEW DOCUMENT with an
    // empty sessionStorage, so this effect used to fire an unauthenticated GET — which 401s,
    // and `auth/client.js` turns any 401 into a session wipe. It also left the store empty for
    // the whole sign-in, which is how the board's demo config came to be written over the
    // brokerage's real one. Keyed on `isAuthenticated`, so it runs when the token lands.
    if (!isAuthenticated) { setState({ loading: false, error: null }); return undefined; }
    let alive = true;
    setState({ loading: true, error: null });
    (async () => {
      try {
        const b = await freight.getBrokerage();
        if (!alive) return;
        // No brokerage, or one that has never saved a board config, leaves the store empty —
        // the board then publishes its own demo config, which is NOT saved (see commitConfig).
        if (b && b.board) hydrate(b.board, { live: !!b.live });
        // ⛔ THE SAVER IS ATTACHED ONLY ONCE THE READ HAS ANSWERED. Attached before it, any
        // commit that happened while the fetch was in flight — including the board's own
        // bootstrap — raced the read and could land after it, so the server held the demo
        // config while the screen showed the real one.
        attachSaver(
          (board) => freight.saveBoardConfig(board),
          { live: (on) => freight.setBrokerageLive(on) },
        );
        setState({ loading: false, error: null });
      } catch (err) {
        if (!alive) return;
        // ⛔ A FAILED READ IS NOT "no config", AND THE SAVER STAYS DETACHED. We do not know
        // what is on the server, so nothing may overwrite it. The board falls back to its demo
        // config, which is `live: false` and cannot place a call, so this fails closed.
        setState({ loading: false, error: err?.message || 'Could not read your brokerage settings.' });
      }
    })();
    return () => { alive = false; attachSaver(null); };
  }, [isAuthenticated]);
  return state;
}

/**
 * ⛔ A RECORDED ERROR NOBODY SHOWS IS A SWALLOWED ERROR. Both of these were kept and neither
 * was rendered: the read failure was discarded by App, and `getSaveError()` was imported only
 * by its own test. This config decides who may approve a carrier and whether Alfred calls.
 */
function ConfigTrouble({ readError }) {
  const [saveError, setSaveError] = useState(() => getSaveError());
  useEffect(() => subscribe(() => setSaveError(getSaveError())), []);
  const msg = readError
    ? `${readError} This screen is showing sample settings, not your brokerage's — and Alfred is dark.`
    : saveError;
  if (!msg) return null;
  return (
    <div style={{ background: '#000', color: '#fff', fontFamily: sans, fontSize: 12.5,
                  padding: '7px 22px', lineHeight: 1.45 }}>{msg}</div>
  );
}

export default function App() {
  const { error: configError } = useBrokerageConfig();
  return (
    <>
      <style>{`
        .alf-choice { transition: border-color .15s, background .15s; }
        .alf-choice:hover { border-color: #000 !important; background: #FAFAFA; }
      `}</style>
      <ConfigTrouble readError={configError} />
      <Routes>
        {/* Public: you cannot require a session in order to start one. */}
      <Route path="/login" element={<Login />} />
      <Route path="/verify" element={<Verify />} />
      {/* A carrier's public driver application. No sign-in: applicants are the public. */}
      <Route path="/apply/:token" element={<Apply />} />
      {/* The opt-in page: a carrier's people agree to calls and texts on their own phone. No sign-in. */}
      <Route path="/opt-in/:token" element={<OptIn />} />

      <Route path="/" element={<RequireAuth><NotForMembers><Chooser /></NotForMembers></RequireAuth>} />
        <Route path="/:vertical" element={<RequireAuth><NotForMembers><VerticalIndex /></NotForMembers></RequireAuth>} />
        {/* Each screen owns its whole viewport: they ship their own header and their
            own nav, so the app deliberately wraps them in nothing. */}
        <Route path="/carrier/setup" element={<RequireAuth><NotForMembers><CarrierSetup /></NotForMembers></RequireAuth>} />
        {/* The board from before the carrier's own pages: our own accounts (the demo) only. */}
        <Route path="/carrier/board" element={<RequireAuth><NotForMembers><CarrierDispatch /></NotForMembers></RequireAuth>} />
        {/* The carrier's own pages: Home, Hiring, Dispatch, Settings (docs/freight-carrier-pages-plan-2026-10.md). */}
        <Route path="/carrier/*" element={<RequireAuth><CarrierApp /></RequireAuth>} />
        <Route path="/broker/setup" element={<RequireAuth><NotForMembers><BrokerOnboarding /></NotForMembers></RequireAuth>} />
        <Route path="/broker/board" element={<RequireAuth><NotForMembers><BrokerBoard /></NotForMembers></RequireAuth>} />
        {/* Our admin: MuleWorks staff only (the server refuses everyone else). */}
        <Route path="/staff" element={<RequireAuth><RequireStaff><StaffCarriers /></RequireStaff></RequireAuth>} />
        <Route path="/staff/carriers/:id" element={<RequireAuth><RequireStaff><StaffCarrierSetup /></RequireStaff></RequireAuth>} />
        <Route path="/staff/setup-email" element={<RequireAuth><RequireStaff><StaffSetupEmail /></RequireStaff></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
